/**
 * Form helpers for create/edit role views.
 */
import type { SelectOption } from '@/components/forms/Select';
import type { InterfaceElement } from '@/types/interface-elements.types';
import type {
  CreateRolePayload,
  Role,
  RoleBinding,
  RoleBindingRule,
  RoleBindingRules,
  RoleGroup,
  RoleInterfaceBindingItem,
  RoleMode,
  RuleEntityFilter,
  RuleReadScope,
  RoleFormState,
  UpdateRolePayload,
} from '@/types/role.types';
import {
  ACTION_CATEGORY_LABELS,
  ACTION_CATEGORY_ORDER,
  MODULE_DISPLAY_LABELS,
  MODULE_ENTITY_FIELDS,
  POSTITS_BOARD_TOGGLE_KEY,
  ROLE_MODE_CONFIG,
  ROLE_STATUS_CONFIG,
  SCOPE_LABELS,
  UNIVERSAL_INTERFACES,
  type ActionCategory,
  type ModuleEntityFields,
} from './role-configs';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  Download,
  Upload,
  CheckCircle,
  UserPlus,
  UserCog,
  XCircle,
  ClipboardList,
  Settings,
  type LucideIcon,
} from 'lucide-react';

/** Icon + color classes for a permission-grid action. */
export interface ActionVisual {
  Icon: LucideIcon;
  iconBgClass: string;
  iconClass: string;
}

/** Actions grouped under one permission-grid category. */
export interface ActionGroup {
  category: ActionCategory;
  label: string;
  actions: InterfaceElement[];
}

/** UUID → menu / action / tab lookup built from interface elements. */
interface ElementLookupMaps {
  menuByUuid: Record<string, string>;
  actionByUuid: Record<string, { iface: string; elementId: string }>;
  tabByUuid: Record<string, { iface: string; elementId: string }>;
}

/** Actions and write scopes keyed by interface then target role. */
type PerRoleActionsMap = Record<
  string,
  Record<string, { actions: Set<string>; writeScopes: Record<string, RuleReadScope | null> }>
>;

/** Accumulators when converting a legacy permission payload into the builder shape. */
interface LegacyConversionBuckets {
  actionsByInterface: Record<string, Set<string>>;
  writeScopesByInterface: Record<string, Record<string, RuleReadScope | null>>;
  tabsByInterface: Record<string, Set<string>>;
  perRoleActionsByInterface: PerRoleActionsMap;
}

/** Read / create / edit / delete batch toggles plus write scopes. */
export interface BatchConfig {
  read: RuleReadScope | null;
  create: boolean;
  edit: boolean;
  editWriteScope: RuleReadScope | null;
  delete: boolean;
  deleteWriteScope: RuleReadScope | null;
}

/** Actions vs tabs split for one module's interface elements. */
export interface SplitModuleInterfaceElements {
  availableActions: InterfaceElement[];
  perRoleActions: InterfaceElement[];
  globalActions: InterfaceElement[];
  availableTabs: InterfaceElement[];
}

/** One permission-grid module with its filtered interface elements. */
export interface ModuleGroup {
  moduleName: string;
  moduleLabel: string;
  icon: string | null;
  interfaceName: string;
  elements: InterfaceElement[];
}

/** Derived read-scope UI: relations, logic, locks, unrestricted. */
export interface ReadScopeViewState {
  selectedRelations: string[];
  logic: 'AND' | 'OR';
  lockedRelations: string[];
  isConstrained: boolean;
  isUnrestricted: boolean;
}

/** Status select: derived from {@link ROLE_STATUS_CONFIG}. */
export const ROLE_FORM_STATUS_OPTIONS: SelectOption[] = Object.entries(ROLE_STATUS_CONFIG).map(
  ([value, cfg]) => ({ value, label: cfg.label }),
);

/** Mode select: unset + derived from {@link ROLE_MODE_CONFIG}. */
export const ROLE_FORM_MODE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Non défini' },
  ...Object.entries(ROLE_MODE_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label })),
];

/** Empty role form; status defaults to active. */
export function emptyRoleForm(): RoleFormState {
  return {
    code: '',
    label: '',
    description: '',
    roleGroup: '',
    hierarchyLevel: '',
    icon: '',
    color: '',
    mode: '',
    status: 'active',
    bindings: {},
  };
}

/** Patch one role-form field. */
export function applyRoleFormField(
  form: RoleFormState,
  field: keyof RoleFormState,
  value: unknown,
): RoleFormState {
  return { ...form, [field]: value };
}

/** Uppercase code with spaces replaced by `_`. */
export function normalizeRoleCode(value: string): string {
  return value.toUpperCase().replaceAll(/\s/g, '_');
}

/** Empty string → `undefined` so hierarchy is omitted from the DTO. */
export function parseHierarchyLevel(value: string): number | undefined {
  return value === '' ? undefined : Number(value);
}

/** Volunteers at level 1 have nobody below them — no post-it planning access. */
export function canHavePostItsConfigAccess(
  roleGroup: string | undefined,
  hierarchyLevel: number | null | undefined,
): boolean {
  if (roleGroup === 'admins') return true;
  if (roleGroup === 'volunteers' && hierarchyLevel === 1) return false;
  return true;
}

/** Create requires a non-empty label and code. */
export function isCreateRoleValid(form: RoleFormState): boolean {
  return form.label.trim().length > 0 && form.code.trim().length > 0;
}

/** Edit requires a non-empty label (code is immutable). */
export function isEditRoleValid(form: RoleFormState): boolean {
  return form.label.trim().length > 0;
}

/** Role-group select options plus an empty "no group" row. */
export function buildRoleGroupOptions(roleGroups: RoleGroup[]): SelectOption[] {
  return [
    { value: '', label: 'Aucun groupe' },
    ...roleGroups.map((g) => ({ value: g.code, label: g.label })),
  ];
}

/** Hydrate form fields from GET role (bindings stay separate). */
export function roleToFormFields(role: Role): Omit<RoleFormState, 'bindings'> {
  return {
    code: role.code ?? '',
    label: role.label ?? '',
    description: role.description ?? '',
    roleGroup: role.roleGroup ?? '',
    hierarchyLevel: role.hierarchyLevel == null ? '' : String(role.hierarchyLevel),
    icon: role.icon ?? '',
    color: role.color ?? '',
    mode: role.mode ?? '',
    status: role.status ?? 'active',
  };
}

/** Create-role DTO; optional fields omitted when empty. */
export function buildCreateRolePayload(form: RoleFormState): CreateRolePayload {
  const payload: CreateRolePayload = {
    code: form.code,
    label: form.label,
    status: form.status || 'active',
  };
  if (form.description) payload.description = form.description;
  if (form.roleGroup) payload.roleGroup = form.roleGroup;
  const hierarchyLevel = parseHierarchyLevel(form.hierarchyLevel);
  if (hierarchyLevel !== undefined) payload.hierarchyLevel = hierarchyLevel;
  if (form.icon) payload.icon = form.icon;
  if (form.color) payload.color = form.color;
  if (form.mode) payload.mode = form.mode;
  return payload;
}

/** Update-role DTO (code is immutable). */
export function buildUpdateRolePayload(form: RoleFormState): UpdateRolePayload {
  const payload: UpdateRolePayload = {
    label: form.label,
    status: form.status || 'active',
  };
  if (form.description !== undefined) payload.description = form.description;
  if (form.roleGroup) payload.roleGroup = form.roleGroup;
  const hierarchyLevel = parseHierarchyLevel(form.hierarchyLevel);
  if (hierarchyLevel !== undefined) payload.hierarchyLevel = hierarchyLevel;
  if (form.icon) payload.icon = form.icon;
  if (form.color) payload.color = form.color;
  if (form.mode) payload.mode = form.mode;
  return payload;
}

/** Central error copy from {@link getErrorMessage}. */
export function getRoleFormErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Human-readable AND/OR preview of scope relations. */
export function getScopePreview(relations: string[], logic: 'AND' | 'OR'): string {
  if (relations.length === 0) return 'Toutes les données';
  const labels = relations.map((rel) => SCOPE_LABELS[rel]?.label || rel).filter(Boolean);
  if (labels.length === 1) return labels[0];
  return labels.join(logic === 'AND' ? ' ET ' : ' OU ');
}

/** writeScope ⊆ visibilityScope — auto-clamp write to stay inside read. */
export function clampWriteScope(
  write: RuleReadScope | null,
  visibility: RuleReadScope | null,
): RuleReadScope | null {
  if (visibility === null) return write;
  if (write === null) return visibility;

  const visibilityRelations = visibility.relations || [];
  const writeRelations = write.relations || [];
  const missingRelations = visibilityRelations.filter((rel) => !writeRelations.includes(rel));
  if (missingRelations.length === 0) return write;

  return {
    relations: Array.from(new Set([...visibilityRelations, ...writeRelations])),
    logic: 'AND',
  };
}

/** Entity-filter fields configured for a module interface. */
export function getModuleEntityFields(interfaceName: string): ModuleEntityFields[] {
  return MODULE_ENTITY_FIELDS[interfaceName] || [];
}

/** Catalog label for a module, or a title-cased fallback. */
export function getModuleDisplayLabel(interfaceName: string): string {
  const mapped = MODULE_DISPLAY_LABELS[interfaceName];
  if (mapped) return mapped;
  return interfaceName
    .replaceAll('-', ' ')
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** `config.subtype` when it is a string. */
export function getActionSubtype(action: InterfaceElement): string | undefined {
  const subtype = action.config?.subtype;
  return typeof subtype === 'string' ? subtype : undefined;
}

/** True when the subtype mentions `role` (create/edit/delete-role). */
export function isRoleActionSubtype(subtype: string | undefined): boolean {
  return !!subtype && subtype.includes('role');
}

/** True for create/edit/delete-role actions (one checkbox per target role). */
export function isPerRoleAction(action: InterfaceElement): boolean {
  const subtype = getActionSubtype(action);
  return subtype === 'create-role' || subtype === 'edit-role' || subtype === 'delete-role';
}

/** Map an interface action onto a permission-grid category. */
export function categorizeAction(action: InterfaceElement): ActionCategory {
  const id = action.elementId;
  const subtype = getActionSubtype(action);

  if (id.includes('view-report')) return 'consultation';
  if (id === 'action-create' || subtype === 'create-role') return 'creation';
  if (
    id.includes('edit-row') ||
    subtype === 'edit-role' ||
    id.includes('cancel-row') ||
    id.includes('cancel') ||
    id.includes('manage-attendance')
  )
    return 'modification';
  if (id.includes('delete-row') || id.includes('option-delete-') || subtype === 'delete-role')
    return 'suppression';
  if (id.includes('export-') || id.includes('import-')) return 'export_import';
  if (id.includes('validate')) return 'validation';
  return 'other';
}

/** Group actions by category, skipping empty groups. */
export function groupActions(actions: InterfaceElement[]): ActionGroup[] {
  const groups = Object.fromEntries(
    ACTION_CATEGORY_ORDER.map((c) => [c, [] as InterfaceElement[]]),
  ) as Record<ActionCategory, InterfaceElement[]>;

  for (const action of actions) groups[categorizeAction(action)].push(action);

  return ACTION_CATEGORY_ORDER.filter((c) => groups[c].length > 0).map((c) => ({
    category: c,
    label: ACTION_CATEGORY_LABELS[c],
    actions: groups[c],
  }));
}

/** Role code encoded in a `tab-*` element id. */
export function tabElementRoleCode(elementId: string): string {
  return elementId.replace(/^tab-/, '');
}

/** Find the create/edit/delete-role action aimed at `roleCode`. */
export function findPerRoleAction(
  actions: InterfaceElement[],
  subtype: string,
  roleCode: string,
): InterfaceElement | undefined {
  return actions.find((a) => {
    const targetRole = a.config?.targetRole;
    const targetRoleCode = typeof targetRole === 'string' ? targetRole : '';
    return (
      getActionSubtype(a) === subtype && targetRoleCode.toLowerCase() === roleCode.toLowerCase()
    );
  });
}

/** True when the rule filters `role` with `in` including `roleCode`. */
export function isRoleTabRule(rule: RoleBindingRule, roleCode: string): boolean {
  return (
    rule.entityFilter?.field === 'role' &&
    rule.entityFilter.operator === 'in' &&
    (rule.entityFilter.values?.includes(roleCode) ?? false)
  );
}

/** First tab rule matching `roleCode`, or null. */
export function findTabRule(rules: RoleBindingRule[], roleCode: string): RoleBindingRule | null {
  return rules.find((r) => isRoleTabRule(r, roleCode)) ?? null;
}

/** Catch-all rule with no entity filter. */
function emptyDefaultRule(): RoleBindingRule {
  return { id: 'rule-default', entityFilter: null, read: null, actions: [] };
}

/** New specific rule with an empty entity filter. */
export function emptySpecificRule(): RoleBindingRule {
  return {
    id: `rule-${Date.now()}`,
    entityFilter: { field: '', operator: 'in', values: [] },
    read: null,
    actions: [],
  };
}

/** Tab rule pre-filtered to `roleCode`. */
export function emptyTabRule(
  roleCode: string,
  updates?: Partial<RoleBindingRule>,
): RoleBindingRule {
  return {
    id: `rule-${roleCode}-${Date.now()}`,
    entityFilter: { field: 'role', operator: 'in', values: [roleCode] },
    read: null,
    actions: [],
    ...updates,
  };
}

/** Fresh module binding; tabs only exist for role-group modules. */
export function emptyModuleBinding(isRoleGroup: boolean): RoleBindingRules {
  return { rules: [emptyDefaultRule()], tabs: isRoleGroup ? [] : undefined };
}

/** Existing binding for the interface, or an empty one inferred from its tabs. */
export function resolveModuleBinding(
  value: Record<string, RoleBindingRules>,
  modules: ModuleGroup[],
  interfaceName: string,
): RoleBindingRules {
  if (value[interfaceName]) return value[interfaceName];
  const module = modules.find((m) => m.interfaceName === interfaceName);
  const isRoleGroup = module?.elements.some((el) => el.elementType === 'tab') ?? false;
  return emptyModuleBinding(isRoleGroup);
}

/** Unique action ids granted across all rules. */
export function countConfiguredActions(binding: RoleBindingRules): number {
  const actionIds = new Set<string>();
  binding.rules.forEach((rule) => rule.actions.forEach((id) => actionIds.add(id)));
  return actionIds.size;
}

/** Interface items → legacy `{ elementId, enabled, visibilityScope }` rows. */
export function toLegacyBindingsFromInterfaceItems(
  items: Pick<RoleInterfaceBindingItem, 'elementId' | 'visibilityScope'>[],
): RoleBinding[] {
  return items.map((b) => ({
    elementId: b.elementId,
    enabled: true,
    visibilityScope: b.visibilityScope || null,
  }));
}

/** Whether the post-its board toggle is visible on this role. */
export function isPostItsBoardToggleVisible(
  items: Pick<RoleInterfaceBindingItem, 'elementKey' | 'visible'>[],
): boolean {
  return items.find((b) => b.elementKey === POSTITS_BOARD_TOGGLE_KEY)?.visible ?? false;
}

/** Icon + colors for a permission-grid action (subtype, then element id). */
export function getActionVisual(action: InterfaceElement): ActionVisual {
  const subtype = action.config?.subtype;
  const id = action.elementId;

  if (subtype === 'create-role') {
    return { Icon: UserPlus, iconBgClass: 'bg-emerald-100', iconClass: 'text-emerald-700' };
  }
  if (subtype === 'edit-role') {
    return { Icon: UserCog, iconBgClass: 'bg-sky-100', iconClass: 'text-sky-700' };
  }
  if (id.includes('create')) {
    return { Icon: Plus, iconBgClass: 'bg-emerald-100', iconClass: 'text-emerald-700' };
  }
  if (id.includes('edit')) {
    return { Icon: Pencil, iconBgClass: 'bg-sky-100', iconClass: 'text-sky-700' };
  }
  if (id.includes('delete')) {
    return { Icon: Trash2, iconBgClass: 'bg-red-100', iconClass: 'text-red-700' };
  }
  if (id.includes('view') || id.includes('report')) {
    return { Icon: Eye, iconBgClass: 'bg-violet-100', iconClass: 'text-violet-700' };
  }
  if (id.includes('export')) {
    return { Icon: Download, iconBgClass: 'bg-amber-100', iconClass: 'text-amber-700' };
  }
  if (id.includes('import')) {
    return { Icon: Upload, iconBgClass: 'bg-teal-100', iconClass: 'text-teal-700' };
  }
  if (id.includes('validate')) {
    return { Icon: CheckCircle, iconBgClass: 'bg-green-100', iconClass: 'text-green-700' };
  }
  if (id.includes('cancel')) {
    return { Icon: XCircle, iconBgClass: 'bg-orange-100', iconClass: 'text-orange-700' };
  }
  if (id.includes('attendance') || id.includes('presence')) {
    return { Icon: ClipboardList, iconBgClass: 'bg-indigo-100', iconClass: 'text-indigo-700' };
  }
  return { Icon: Settings, iconBgClass: 'bg-gray-100', iconClass: 'text-gray-600' };
}

/** Build an enabled menu binding from the given rules. */
function buildMenuBinding(menuEl: InterfaceElement, rules: RoleBindingRule[]): RoleBinding {
  return {
    elementId: menuEl.id,
    enabled: true,
    visibilityScope: convertRulesToLegacyScope(rules),
  };
}

/** Build enabled action bindings from the selected rule actions. */
function buildActionBindings(
  elements: InterfaceElement[],
  rules: RoleBindingRule[],
): RoleBinding[] {
  const varcharIds = new Set<string>();
  const writeScopesByAction: Record<string, RuleReadScope | null> = {};

  for (const rule of rules) {
    for (const id of rule.actions) varcharIds.add(id);
    if (rule.writeScopes) Object.assign(writeScopesByAction, rule.writeScopes);
  }

  if (elements.some((e) => e.elementId === 'action-view-row' && e.elementType === 'action')) {
    varcharIds.add('action-view-row');
  }

  const hasCreateRoleActions = Array.from(varcharIds).some((id) => id.startsWith('option-create-'));
  if (
    hasCreateRoleActions &&
    elements.some((e) => e.elementId === 'action-create' && e.elementType === 'action')
  ) {
    varcharIds.add('action-create');
  }

  const result: RoleBinding[] = [];
  for (const varcharId of varcharIds) {
    const el = elements.find((e) => e.elementId === varcharId && e.elementType === 'action');
    if (!el) continue;
    const ws = writeScopesByAction[varcharId] ?? null;
    result.push({
      elementId: el.id,
      enabled: true,
      visibilityScope: ws
        ? convertRulesToLegacyScope([
            { id: 'rule-default', entityFilter: null, read: ws, actions: [] },
          ])
        : null,
    });
  }
  return result;
}

/** Enable every interface element of the given type. */
function buildElementTypeBindings(elements: InterfaceElement[], type: 'filter'): RoleBinding[] {
  return elements
    .filter((el) => el.elementType === type)
    .map((el) => ({ elementId: el.id, enabled: true, visibilityScope: null }));
}

/** Build tab bindings with optional selection and read scope. */
function buildTabBindingsWithScope(
  elements: InterfaceElement[],
  rules: RoleBindingRule[],
  selectedTabs?: string[] | null,
): RoleBinding[] {
  const tabs = elements.filter((el) => el.elementType === 'tab');
  const filtered =
    selectedTabs != null ? tabs.filter((el) => selectedTabs.includes(el.elementId)) : tabs;

  return filtered.map((tabEl) => {
    const roleCode = tabElementRoleCode(tabEl.elementId);
    const tabRule = findTabRule(rules, roleCode);
    const visibilityScope = tabRule?.read
      ? convertRulesToLegacyScope([{ ...tabRule, actions: [] }])
      : null;
    return { elementId: tabEl.id, enabled: true, visibilityScope };
  });
}

/** Flatten the rules-editor map into the API's legacy binding list. */
export function convertRulesToLegacyBindings(
  rulesMap: Record<string, RoleBindingRules>,
  interfaceElements: Record<string, InterfaceElement[]>,
): RoleBinding[] {
  const bindings: RoleBinding[] = [];
  const sidebarElements = interfaceElements.sidebar ?? [];
  const addedSidebarUuids = new Set<string>();

  for (const [interfaceName, rulesData] of Object.entries(rulesMap)) {
    if (UNIVERSAL_INTERFACES.has(interfaceName)) continue;
    const elements = interfaceElements[interfaceName] ?? [];
    if (elements.length === 0) continue;

    const menuEl = elements.find((el) => el.elementType === 'menu');
    if (menuEl) bindings.push(buildMenuBinding(menuEl, rulesData.rules));

    const sidebarMenuEl = sidebarElements.find((el) => el.elementId === `menu-${interfaceName}`);
    if (sidebarMenuEl && !addedSidebarUuids.has(sidebarMenuEl.id)) {
      bindings.push({ elementId: sidebarMenuEl.id, enabled: true, visibilityScope: null });
      addedSidebarUuids.add(sidebarMenuEl.id);
    }

    bindings.push(
      ...buildActionBindings(elements, rulesData.rules),
      ...buildElementTypeBindings(elements, 'filter'),
      ...buildTabBindingsWithScope(elements, rulesData.rules, rulesData.tabs),
    );
  }

  return bindings;
}

/** Flatten editor rules into a legacy visibility scope. */
function convertRulesToLegacyScope(rules: RoleBindingRule[]): RoleBinding['visibilityScope'] {
  if (rules.length === 0) return null;

  const defaultRule = rules.find((r) => !r.entityFilter);
  const specificRules = rules.filter((r) => r.entityFilter);
  const topLevelRelations = defaultRule?.read?.relations || [];
  const topLevelOperator = defaultRule?.read?.logic || 'OR';
  const entityConditions = specificRules.map((rule) => ({
    field: rule.entityFilter!.field,
    operator: rule.entityFilter!.operator as 'equals' | 'in',
    values: rule.entityFilter!.values,
    scope: rule.read ? { relations: rule.read.relations, operator: rule.read.logic } : undefined,
  }));

  if (topLevelRelations.length === 0 && entityConditions.length === 0) return null;

  return {
    relations: topLevelRelations,
    operator: topLevelOperator,
    entityConditions: entityConditions.length > 0 ? entityConditions : undefined,
  };
}

/** Index menu, action, and tab elements by UUID. */
function buildElementLookupMaps(
  interfaceElements: Record<string, InterfaceElement[]>,
): ElementLookupMaps {
  const menuByUuid: Record<string, string> = {};
  const actionByUuid: Record<string, { iface: string; elementId: string }> = {};
  const tabByUuid: Record<string, { iface: string; elementId: string }> = {};
  for (const [interfaceName, elements] of Object.entries(interfaceElements)) {
    if (UNIVERSAL_INTERFACES.has(interfaceName)) continue;
    for (const el of elements) {
      if (el.elementType === 'menu') menuByUuid[el.id] = interfaceName;
      if (el.elementType === 'action')
        actionByUuid[el.id] = { iface: interfaceName, elementId: el.elementId };
      if (el.elementType === 'tab')
        tabByUuid[el.id] = { iface: interfaceName, elementId: el.elementId };
    }
  }
  return { menuByUuid, actionByUuid, tabByUuid };
}

/** Copy collected action ids onto each interface default rule. */
function injectActionsIntoRules(
  rulesMap: Record<string, RoleBindingRules>,
  actionsByInterface: Record<string, Set<string>>,
  writeScopesByInterface: Record<string, Record<string, RuleReadScope | null>>,
): void {
  for (const [interfaceName, actionIds] of Object.entries(actionsByInterface)) {
    if (actionIds.size === 0) continue;
    if (!rulesMap[interfaceName]) {
      rulesMap[interfaceName] = {
        rules: [{ id: 'rule-default', entityFilter: null, read: null, actions: [] }],
      };
    }
    let defaultRule = rulesMap[interfaceName].rules.find((r) => !r.entityFilter);
    if (!defaultRule) {
      defaultRule = { id: 'rule-default', entityFilter: null, read: null, actions: [] };
      rulesMap[interfaceName].rules.push(defaultRule);
    }
    defaultRule.actions = Array.from(actionIds);
    defaultRule.writeScopes = writeScopesByInterface[interfaceName] || {};
  }
}

/** Map a legacy visibility scope to a rule read scope. */
function scopeFromBinding(vs: NonNullable<RoleBinding['visibilityScope']>): RuleReadScope {
  return {
    relations: vs.relations || resolveRelationsFromScope(vs),
    logic: (vs.operator || vs.logic || 'OR') as 'AND' | 'OR',
  };
}

/** Record an enabled action onto interface or per-role buckets. */
function applyEnabledActionBinding(
  action: { iface: string; elementId: string },
  binding: RoleBinding,
  elementConfigByVarcharId: Record<string, Record<string, unknown>>,
  actionsByInterface: Record<string, Set<string>>,
  writeScopesByInterface: Record<string, Record<string, RuleReadScope | null>>,
  perRoleActionsByInterface: PerRoleActionsMap,
): void {
  const actionConfig = elementConfigByVarcharId[action.elementId] || {};
  const targetRole = actionConfig.targetRole as string | undefined;
  if (targetRole) {
    if (!perRoleActionsByInterface[action.iface][targetRole]) {
      perRoleActionsByInterface[action.iface][targetRole] = {
        actions: new Set(),
        writeScopes: {},
      };
    }
    perRoleActionsByInterface[action.iface][targetRole].actions.add(action.elementId);
    if (binding.visibilityScope) {
      perRoleActionsByInterface[action.iface][targetRole].writeScopes[action.elementId] =
        scopeFromBinding(binding.visibilityScope);
    }
    return;
  }
  actionsByInterface[action.iface]?.add(action.elementId);
  if (binding.visibilityScope) {
    writeScopesByInterface[action.iface][action.elementId] = scopeFromBinding(
      binding.visibilityScope,
    );
  }
}

/** Record an enabled tab and optional read scope. */
function applyEnabledTabBinding(
  tab: { iface: string; elementId: string },
  binding: RoleBinding,
  rulesMap: Record<string, RoleBindingRules>,
  tabsByInterface: Record<string, Set<string>>,
): void {
  tabsByInterface[tab.iface]?.add(tab.elementId);
  if (!binding.visibilityScope) return;
  const roleCode = tabElementRoleCode(tab.elementId);
  if (!rulesMap[tab.iface]) rulesMap[tab.iface] = { rules: [] };
  const readScope = convertLegacyScopeToRules(binding.visibilityScope)[0]?.read || null;
  const tabRule = findTabRule(rulesMap[tab.iface].rules, roleCode);
  if (!tabRule) {
    rulesMap[tab.iface].rules.push(emptyTabRule(roleCode, { read: readScope }));
    return;
  }
  if (!tabRule.read) tabRule.read = readScope;
}

/** Merge per-role action sets into tab rules. */
function mergePerRoleActionsIntoRules(
  rulesMap: Record<string, RoleBindingRules>,
  perRoleActionsByInterface: PerRoleActionsMap,
): void {
  for (const [interfaceName, roleActionsMap] of Object.entries(perRoleActionsByInterface)) {
    if (Object.keys(roleActionsMap).length === 0) continue;
    if (!rulesMap[interfaceName]) rulesMap[interfaceName] = { rules: [] };
    for (const [targetRole, { actions, writeScopes }] of Object.entries(roleActionsMap)) {
      let tabRule = findTabRule(rulesMap[interfaceName].rules, targetRole);
      if (!tabRule) {
        tabRule = emptyTabRule(targetRole);
        rulesMap[interfaceName].rules.push(tabRule);
      }
      tabRule.actions = Array.from(new Set([...(tabRule.actions ?? []), ...actions]));
      tabRule.writeScopes = { ...tabRule.writeScopes, ...writeScopes };
    }
  }
}

/** Persist a partial tab selection when not all tabs are enabled. */
function assignPartialTabs(
  rulesMap: Record<string, RoleBindingRules>,
  tabsByInterface: Record<string, Set<string>>,
  interfaceElements: Record<string, InterfaceElement[]>,
): void {
  for (const [interfaceName, selectedTabIds] of Object.entries(tabsByInterface)) {
    if (!rulesMap[interfaceName]) continue;
    const allTabsInInterface =
      interfaceElements[interfaceName]
        ?.filter((el) => el.elementType === 'tab')
        .map((el) => el.elementId) ?? [];
    if (allTabsInInterface.length === 0) continue;
    if (selectedTabIds.size < allTabsInInterface.length) {
      rulesMap[interfaceName].tabs = Array.from(selectedTabIds);
    }
  }
}

/** Empty accumulators for converting a legacy permission payload. */
function emptyConversionBuckets(
  interfaceElements: Record<string, InterfaceElement[]>,
): LegacyConversionBuckets {
  const buckets: LegacyConversionBuckets = {
    actionsByInterface: {},
    writeScopesByInterface: {},
    tabsByInterface: {},
    perRoleActionsByInterface: {},
  };
  for (const interfaceName of Object.keys(interfaceElements)) {
    buckets.actionsByInterface[interfaceName] = new Set();
    buckets.writeScopesByInterface[interfaceName] = {};
    buckets.tabsByInterface[interfaceName] = new Set();
    buckets.perRoleActionsByInterface[interfaceName] = {};
  }
  return buckets;
}

/** Index action element configs by varchar element id. */
function indexActionConfigs(
  interfaceElements: Record<string, InterfaceElement[]>,
): Record<string, Record<string, unknown>> {
  const elementConfigByVarcharId: Record<string, Record<string, unknown>> = {};
  for (const elements of Object.values(interfaceElements)) {
    for (const el of elements) {
      if (el.elementType === 'action' && el.elementId) {
        elementConfigByVarcharId[el.elementId] = (el.config as Record<string, unknown>) || {};
      }
    }
  }
  return elementConfigByVarcharId;
}

/** Apply one legacy binding onto the conversion buckets. */
function applyLegacyBinding(
  binding: RoleBinding,
  lookups: ElementLookupMaps,
  buckets: LegacyConversionBuckets,
  elementConfigByVarcharId: Record<string, Record<string, unknown>>,
  rulesMap: Record<string, RoleBindingRules>,
): void {
  const interfaceName = lookups.menuByUuid[binding.elementId];
  if (interfaceName) {
    rulesMap[interfaceName] = { rules: convertLegacyScopeToRules(binding.visibilityScope) };
    return;
  }

  const action = lookups.actionByUuid[binding.elementId];
  if (action && binding.enabled) {
    applyEnabledActionBinding(
      action,
      binding,
      elementConfigByVarcharId,
      buckets.actionsByInterface,
      buckets.writeScopesByInterface,
      buckets.perRoleActionsByInterface,
    );
  }

  const tab = lookups.tabByUuid[binding.elementId];
  if (tab && binding.enabled) {
    applyEnabledTabBinding(tab, binding, rulesMap, buckets.tabsByInterface);
  }
}

/** Hydrate the rules editor from the API's legacy binding list. */
export function convertLegacyBindingsToRules(
  bindings: RoleBinding[],
  interfaceElements: Record<string, InterfaceElement[]>,
): Record<string, RoleBindingRules> {
  const rulesMap: Record<string, RoleBindingRules> = {};
  const lookups = buildElementLookupMaps(interfaceElements);
  const buckets = emptyConversionBuckets(interfaceElements);
  const elementConfigByVarcharId = indexActionConfigs(interfaceElements);

  for (const binding of bindings) {
    applyLegacyBinding(binding, lookups, buckets, elementConfigByVarcharId, rulesMap);
  }

  injectActionsIntoRules(rulesMap, buckets.actionsByInterface, buckets.writeScopesByInterface);
  mergePerRoleActionsIntoRules(rulesMap, buckets.perRoleActionsByInterface);
  assignPartialTabs(rulesMap, buckets.tabsByInterface, interfaceElements);
  return rulesMap;
}

/** Resolve relation keys from a legacy scope shorthand. */
function resolveRelationsFromScope(scope: { relations?: string[]; scope?: string }): string[] {
  if (scope.relations && scope.relations.length > 0) return scope.relations;
  switch (scope.scope) {
    case 'same_establishment':
      return ['establishment'];
    case 'managed_by':
      return ['managedBy'];
    case 'same_establishment_or_managed_by':
      return ['establishment', 'managedBy'];
    case 'assignment':
      return ['assignment'];
    case 'same_program':
      return ['program'];
    case 'same_department':
      return ['department'];
    case 'relation_overlap':
      return scope.relations || [];
    default:
      return [];
  }
}

/** Convert one entity condition into a role-binding rule. */
function entityConditionToRule(
  cond: {
    field: string;
    operator?: 'equals' | 'in';
    values: string[];
    scope?: { relations?: string[]; operator?: 'AND' | 'OR'; logic?: 'AND' | 'OR'; scope?: string };
  },
  index: number,
): RoleBindingRule {
  return {
    id: `rule-${index}`,
    entityFilter: {
      field: cond.field,
      operator: cond.operator || 'in',
      values: cond.values,
    },
    read: cond.scope
      ? {
          relations: resolveRelationsFromScope(cond.scope),
          logic: (cond.scope.operator || cond.scope.logic || 'OR') as 'AND' | 'OR',
        }
      : null,
    actions: [],
  };
}

/** Expand a legacy visibility scope into editor rules. */
function convertLegacyScopeToRules(
  scope: RoleBinding['visibilityScope'] | undefined,
): RoleBindingRule[] {
  if (!scope || (typeof scope === 'object' && Object.keys(scope).length === 0)) {
    return [{ id: 'rule-default', entityFilter: null, read: null, actions: [] }];
  }
  const scopeRecord = scope as RoleBinding['visibilityScope'] & { scope?: string };
  if (scopeRecord?.scope === 'unrestricted') {
    return [{ id: 'rule-default', entityFilter: null, read: null, actions: [] }];
  }
  const relations = resolveRelationsFromScope(scope);
  const logic = (scope.operator || scope.logic || 'OR') as 'AND' | 'OR';
  const defaultRule: RoleBindingRule = {
    id: 'rule-default',
    entityFilter: null,
    read: relations.length > 0 ? { relations, logic } : null,
    actions: [],
  };
  const specificRules: RoleBindingRule[] = scope.entityConditions
    ? scope.entityConditions.map((cond, index) => entityConditionToRule(cond, index))
    : [];
  return [defaultRule, ...specificRules];
}

/** Action ids + write scopes for the per-role create/edit/delete batch. */
export function buildBatchRulePatch(
  cfg: BatchConfig,
  perRoleActions: InterfaceElement[],
  roleCode: string,
): { actions: string[]; writeScopes: Record<string, RuleReadScope | null> } {
  const createAction = findPerRoleAction(perRoleActions, 'create-role', roleCode);
  const editAction = findPerRoleAction(perRoleActions, 'edit-role', roleCode);
  const deleteAction = findPerRoleAction(perRoleActions, 'delete-role', roleCode);
  const actions: string[] = [];
  const writeScopes: Record<string, RuleReadScope | null> = {};
  if (cfg.create && createAction) actions.push(createAction.elementId);
  if (cfg.edit && editAction) {
    actions.push(editAction.elementId);
    if (cfg.editWriteScope !== null) writeScopes[editAction.elementId] = cfg.editWriteScope;
  }
  if (cfg.delete && deleteAction) {
    actions.push(deleteAction.elementId);
    if (cfg.deleteWriteScope !== null) writeScopes[deleteAction.elementId] = cfg.deleteWriteScope;
  }
  return { actions, writeScopes };
}

/** Merge a batch patch into the matching tab rule, or append a new one. */
export function upsertTabBatchRule(
  rules: RoleBindingRule[],
  roleCode: string,
  patch: {
    read: RuleReadScope | null;
    actions: string[];
    writeScopes: Record<string, RuleReadScope | null>;
  },
): void {
  const existingIdx = rules.findIndex((r) => isRoleTabRule(r, roleCode));
  if (existingIdx >= 0) {
    rules[existingIdx] = { ...rules[existingIdx], ...patch };
    return;
  }
  rules.push(emptyTabRule(roleCode, patch));
}

/** Split interface elements into per-role, global, and tab lists. */
export function splitModuleInterfaceElements(
  elements: InterfaceElement[],
): SplitModuleInterfaceElements {
  const availableActions = elements.filter(
    (el) => el.elementType === 'action' && el.elementId !== 'action-view-row',
  );
  const perRoleActions = availableActions.filter(isPerRoleAction);
  const globalActions = availableActions.filter((a) => !perRoleActions.includes(a));
  const availableTabs = elements.filter((el) => el.elementType === 'tab');
  return { availableActions, perRoleActions, globalActions, availableTabs };
}

/** Null/undefined tabs means every tab is selected. */
export function isModuleTabSelected(
  tabs: string[] | null | undefined,
  tabElementId: string,
): boolean {
  if (tabs === undefined || tabs === null) return true;
  return tabs.includes(tabElementId);
}

/** Toggle a tab id; all selected → `null` (unrestricted). */
export function computeModuleTabsAfterToggle(
  tabs: string[] | null | undefined,
  tabElementId: string,
  availableTabIds: string[],
): string[] | null {
  const currentTabs = tabs ?? availableTabIds;
  if (currentTabs.includes(tabElementId)) {
    const newTabs = currentTabs.filter((id) => id !== tabElementId);
    return newTabs.length === 0 ? [] : newTabs;
  }
  const newTabs = [...currentTabs, tabElementId];
  return newTabs.length === availableTabIds.length ? null : newTabs;
}

/** Patch batch toggles and re-clamp write scopes when read changes. */
export function applyBatchConfigPatch(
  current: BatchConfig,
  patch: Partial<BatchConfig>,
): BatchConfig {
  const next = { ...current, ...patch };
  if (patch.read !== undefined) {
    next.editWriteScope = clampWriteScope(next.editWriteScope, next.read);
    next.deleteWriteScope = clampWriteScope(next.deleteWriteScope, next.read);
  }
  return next;
}

/** Group interface elements by module, skipping universal interfaces. */
export function buildModuleGroups(
  groupedElements: Record<string, InterfaceElement[]>,
  mode?: RoleMode | null,
): ModuleGroup[] {
  return Object.entries(groupedElements)
    .filter(([moduleName]) => !UNIVERSAL_INTERFACES.has(moduleName))
    .map(([moduleName, elements]) => {
      const filteredElements = mode
        ? elements.filter((el) => !el.mode || el.mode === mode)
        : elements;
      const firstElement = filteredElements[0];
      const interfaceName = firstElement?.interfaceName || moduleName;
      return {
        moduleName,
        moduleLabel: getModuleDisplayLabel(interfaceName),
        icon: firstElement?.groupIcon || null,
        interfaceName,
        elements: filteredElements,
      };
    })
    .filter((m) => m.elements.length > 0);
}

/** Derive read-scope view state from the rule and an optional parent constraint. */
export function deriveReadScopeViewState(
  value: RuleReadScope | null | undefined,
  constrainedBy?: RuleReadScope | null,
): ReadScopeViewState {
  const selectedRelations = value?.relations || [];
  const logic = value?.logic || 'OR';
  const lockedRelations = constrainedBy?.relations || [];
  return {
    selectedRelations,
    logic,
    lockedRelations,
    isConstrained: lockedRelations.length > 0,
    isUnrestricted: selectedRelations.length === 0,
  };
}

/** Toggle a relation; locked parent relations stay selected. */
export function applyReadScopeRelationToggle(
  value: RuleReadScope | null | undefined,
  relationCode: string,
  constrainedBy?: RuleReadScope | null,
): RuleReadScope | null {
  const selectedRelations = value?.relations || [];
  const logic = value?.logic || 'OR';
  const lockedRelations = constrainedBy?.relations || [];
  const isConstrained = lockedRelations.length > 0;
  const extraRelations = selectedRelations.filter((r) => !lockedRelations.includes(r));

  if (lockedRelations.includes(relationCode)) return value ?? null;

  const newExtraRelations = extraRelations.includes(relationCode)
    ? extraRelations.filter((r) => r !== relationCode)
    : [...extraRelations, relationCode];

  if (isConstrained && newExtraRelations.length === 0) {
    return constrainedBy ?? null;
  }
  if (isConstrained) {
    return { relations: [...lockedRelations, ...newExtraRelations], logic: 'AND' };
  }
  if (newExtraRelations.length === 0) {
    return null;
  }
  return { relations: newExtraRelations, logic };
}

/** Flip AND/OR when at least two relations are selected. */
export function applyReadScopeLogicToggle(
  value: RuleReadScope | null | undefined,
): RuleReadScope | null {
  const selectedRelations = value?.relations || [];
  const logic = value?.logic || 'OR';
  if (selectedRelations.length < 2) return value ?? null;
  return { relations: selectedRelations, logic: logic === 'AND' ? 'OR' : 'AND' };
}

/** Keep per-role actions that target `targetRoleCode` (or all when unset). */
export function filterActionsByTargetRole(
  actions: InterfaceElement[],
  targetRoleCode?: string,
): InterfaceElement[] {
  if (!targetRoleCode) return actions;
  return actions.filter((a) => {
    const subtype = getActionSubtype(a);
    if (!isRoleActionSubtype(subtype)) return true;
    const actionTargetRole = a.config?.targetRole;
    return actionTargetRole === targetRoleCode;
  });
}

/** Split actions into regular vs create/edit/delete-role buckets. */
export function partitionActionsByRoleSubtype(filteredActions: InterfaceElement[]): {
  regularActions: InterfaceElement[];
  createRoleActions: InterfaceElement[];
  editRoleActions: InterfaceElement[];
  deleteRoleActions: InterfaceElement[];
} {
  return {
    regularActions: filteredActions.filter((a) => !isRoleActionSubtype(getActionSubtype(a))),
    createRoleActions: filteredActions.filter((a) => getActionSubtype(a) === 'create-role'),
    editRoleActions: filteredActions.filter((a) => getActionSubtype(a) === 'edit-role'),
    deleteRoleActions: filteredActions.filter((a) => getActionSubtype(a) === 'delete-role'),
  };
}

/** Toggle an action id and seed/clear its write scope from visibility. */
export function toggleActionWithWriteScopes(
  selectedActionIds: string[],
  actionId: string,
  writeScopes: Record<string, RuleReadScope | null>,
  visibilityScope?: RuleReadScope | null,
): {
  selectedActionIds: string[];
  writeScopes: Record<string, RuleReadScope | null>;
  writeScopesChanged: boolean;
} {
  const newSelection = selectedActionIds.includes(actionId)
    ? selectedActionIds.filter((id) => id !== actionId)
    : [...selectedActionIds, actionId];

  let nextWriteScopes = writeScopes;

  if (newSelection.includes(actionId) && !selectedActionIds.includes(actionId)) {
    if (visibilityScope && writeScopes[actionId] === undefined) {
      nextWriteScopes = { ...writeScopes, [actionId]: visibilityScope };
    }
  }

  if (!newSelection.includes(actionId) && writeScopes[actionId] !== undefined) {
    nextWriteScopes = { ...nextWriteScopes };
    delete nextWriteScopes[actionId];
  }

  return {
    selectedActionIds: newSelection,
    writeScopes: nextWriteScopes,
    writeScopesChanged: nextWriteScopes !== writeScopes,
  };
}

/** Check/uncheck a tab action and seed/clear its write scope from read. */
export function toggleTabPermAction(
  selectedActions: string[],
  writeScopes: Record<string, RuleReadScope | null>,
  actionId: string,
  checked: boolean,
  readScope: RuleReadScope | null | undefined,
): { actions: string[]; writeScopes: Record<string, RuleReadScope | null> } {
  const newActions = checked
    ? [...selectedActions, actionId]
    : selectedActions.filter((id) => id !== actionId);

  const newWriteScopes = { ...writeScopes };
  if (!checked) {
    delete newWriteScopes[actionId];
  } else if (readScope && newWriteScopes[actionId] === undefined) {
    newWriteScopes[actionId] = readScope;
  }

  return { actions: newActions, writeScopes: newWriteScopes };
}

/** Card title: general, custom label, or field-specific. */
export function ruleCardTitle(
  isDefault: boolean | undefined,
  cardLabel: string | undefined,
  field: string | undefined,
): string {
  if (isDefault) return 'Règle générale';
  if (cardLabel) return cardLabel;
  return field ? `Règle spécifique (${field})` : 'Règle spécifique';
}

/** Clamp every write scope to stay inside read; null when unchanged. */
export function clampRuleWriteScopes(
  writeScopes: Record<string, RuleReadScope | null> | undefined,
  read: RuleReadScope | null,
): Record<string, RuleReadScope | null> | null {
  if (!writeScopes || Object.keys(writeScopes).length === 0) return null;

  const clampedWriteScopes: Record<string, RuleReadScope | null> = {};
  let hasChanges = false;

  for (const [actionId, writeScope] of Object.entries(writeScopes)) {
    const clamped = clampWriteScope(writeScope, read);
    clampedWriteScopes[actionId] = clamped;
    if (JSON.stringify(clamped) !== JSON.stringify(writeScope)) {
      hasChanges = true;
    }
  }

  return hasChanges ? clampedWriteScopes : null;
}

/** New `in` filter for a field (empty values until the user picks). */
export function applyEntityFilterFieldChange(field: string): RuleEntityFilter | null {
  if (!field) return null;
  return {
    field,
    operator: 'in',
    values: [],
  };
}

/** Add or drop one value on the current entity filter. */
export function toggleEntityFilterValue(
  selectedField: string,
  selectedValues: string[],
  val: string,
): RuleEntityFilter {
  const newValues = selectedValues.includes(val)
    ? selectedValues.filter((v) => v !== val)
    : [...selectedValues, val];
  return {
    field: selectedField,
    operator: 'in',
    values: newValues,
  };
}

/** Hydrate form fields + bindings from GET role and interface elements. */
export function hydrateRoleFormFromGet(
  role: Role,
  existingBindings: RoleInterfaceBindingItem[],
  interfaceElements: Record<string, InterfaceElement[]>,
): { form: RoleFormState; postitConfigVisible: boolean } {
  return {
    postitConfigVisible: isPostItsBoardToggleVisible(existingBindings),
    form: {
      ...roleToFormFields(role),
      bindings: convertLegacyBindingsToRules(
        toLegacyBindingsFromInterfaceItems(existingBindings),
        interfaceElements,
      ),
    },
  };
}
